import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { StyleInfo, styleMap } from 'lit/directives/style-map';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { TeListItem } from './te-list-item';

/**
 * List Component
 *
 * @slot - Default slot for list items
 */
@customElement('te-list')
export class TeList extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-list {
        display: flex;
        flex-direction: column;
        background: ${theme.colors.shade0};
        min-height: 48px;
        border-radius: 4px;
        overflow: hidden;
        border: 1px solid ${theme.colors.shade2};
        width: 100%;
        flex: 1;
      }

      ::slotted(*) {
        border-bottom: 1px solid ${theme.colors.shade2};
      }

      ::slotted(*:first-child) {
        border-radius: 4px 4px 0 0;
      }

      ::slotted(*:last-child) {
        border-radius: 0 0 4px 4px;
        border-bottom: none;
      }
    `,
  ];

  /** Minimum width for list */
  @property({ type: Number }) minWidth = 448;
  /** Maximum width for list */
  @property({ type: Number }) maxWidth?: number;

  @state() rowHeight = 48;

  setRowHeight(height: number) {
    if (height > this.rowHeight) {
      this.rowHeight = height;
    }
  }

  updated() {
    (Array.from(this.children) as TeListItem[]).forEach((child) =>
      child.setRowHeight(this.rowHeight)
    );
  }

  getStyle(): StyleInfo {
    if (this.maxWidth === undefined) return { minWidth: this.minWidth + 'px' };
    return {
      minWidth: Math.min(this.minWidth, this.maxWidth) + 'px',
      maxWidth: this.maxWidth + 'px',
    };
  }

  render() {
    return html`
      <div class="te-list" style=${styleMap(this.getStyle())}>
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-list': TeList;
  }
}
